import { Component, ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";


type Props = { children: ReactNode; label?: string };
type State = { error: Error | null; info: string | null; caughtAt: string | null };

// Build id / commit — populated at build time via Vite define if available,
// otherwise falls back to the script filename hash in the DOM.
const BUILD_ID: string =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (import.meta as any).env?.VITE_BUILD_ID ||
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (import.meta as any).env?.VITE_COMMIT_SHA ||
  (typeof document !== "undefined"
    ? (document.querySelector('script[src*="/assets/index-"]') as HTMLScriptElement | null)?.src ?? "unknown"
    : "unknown");

/**
 * Diagnostic error boundary for admin workspaces.
 *
 * Renders the FULL exception (name, message, stack, component stack) plus
 * route, build id, timestamp, and user agent — inline, unminified fields —
 * so a production crash can be triaged without a debugger attached.
 */
export class AdminErrorBoundary extends Component<Props, State & { fallbackReport: string | null }> {
  state: State & { fallbackReport: string | null } = { error: null, info: null, caughtAt: null, fallbackReport: null };


  static getDerivedStateFromError(error: Error): State {
    return { error, info: null, caughtAt: new Date().toISOString() };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    this.setState({
      error,
      info: info?.componentStack ?? null,
      caughtAt: new Date().toISOString(),
    });
    // eslint-disable-next-line no-console
    console.error("[AdminErrorBoundary]", this.props.label ?? "", {
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      componentStack: info?.componentStack,
      route: typeof window !== "undefined" ? window.location.pathname + window.location.search : "",
      buildId: BUILD_ID,
      timestamp: new Date().toISOString(),
    });
  }

  reset = () => this.setState({ error: null, info: null, caughtAt: null });

  buildReport(): string {
    const { error, info, caughtAt } = this.state;
    return [
      `Label: ${this.props.label ?? "(unlabeled)"}`,
      `Name: ${error?.name ?? "(no name)"}`,
      `Message: ${error?.message ?? String(error)}`,
      `Route: ${typeof window !== "undefined" ? window.location.pathname + window.location.search : "(no window)"}`,
      `Build: ${BUILD_ID}`,
      `Timestamp: ${caughtAt ?? new Date().toISOString()}`,
      `UserAgent: ${typeof navigator !== "undefined" ? navigator.userAgent : "(no navigator)"}`,
      "",
      "Stack:",
      String(error?.stack ?? "(no stack)"),
      "",
      "Component stack:",
      info ?? "(none)",
    ].join("\n");
  }

  copyReport = async () => {
    const report = this.buildReport();
    const canUseClipboard =
      typeof navigator !== "undefined" &&
      !!navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function" &&
      (typeof window === "undefined" || window.isSecureContext !== false);

    if (canUseClipboard) {
      try {
        await navigator.clipboard.writeText(report);
        toast.success("Error report copied.");
        return;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[AdminErrorBoundary] clipboard.writeText failed", err);
      }
    }

    // Legacy fallback: hidden textarea + execCommand("copy").
    try {
      if (typeof document !== "undefined") {
        const ta = document.createElement("textarea");
        ta.value = report;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) {
          toast.success("Error report copied.");
          return;
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[AdminErrorBoundary] execCommand copy failed", err);
    }

    // Final fallback: show a manual-copy panel with the full report preselected.
    this.setState({ fallbackReport: report });
    toast.error("Clipboard unavailable. Please copy manually.");
  };


  render() {
    if (!this.state.error) return this.props.children;
    const { error, info, caughtAt } = this.state;
    const name = error?.name || "Error";
    const message = error?.message || String(error);
    const stack = String(error?.stack ?? "");
    const route =
      typeof window !== "undefined" ? window.location.pathname + window.location.search : "";

    return (
      <div className="paper-card p-6 space-y-4 border border-red-300 bg-red-50/60">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
          <div className="space-y-1 min-w-0">
            <h3 className="font-heading text-base font-semibold">
              {this.props.label ?? "Workspace"} crashed
            </h3>
            <p className="text-sm text-red-900 font-mono break-words">
              <span className="font-bold">{name}:</span> {message}
            </p>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 text-xs font-mono">
          <div className="rounded border border-red-200 bg-white/70 p-2">
            <div className="text-red-800 font-semibold">Route</div>
            <div className="break-all">{route || "(unknown)"}</div>
          </div>
          <div className="rounded border border-red-200 bg-white/70 p-2">
            <div className="text-red-800 font-semibold">Timestamp</div>
            <div className="break-all">{caughtAt ?? ""}</div>
          </div>
          <div className="rounded border border-red-200 bg-white/70 p-2 sm:col-span-2">
            <div className="text-red-800 font-semibold">Build</div>
            <div className="break-all">{BUILD_ID}</div>
          </div>
        </div>

        <div className="rounded border border-red-200 bg-white/70 p-3 text-xs">
          <div className="font-semibold text-red-800 mb-1">Stack trace</div>
          <pre className="whitespace-pre-wrap break-words max-h-64 overflow-auto">
{stack || "(no stack)"}
          </pre>
        </div>

        {info ? (
          <div className="rounded border border-red-200 bg-white/70 p-3 text-xs">
            <div className="font-semibold text-red-800 mb-1">Component stack</div>
            <pre className="whitespace-pre-wrap break-words max-h-48 overflow-auto">{info}</pre>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={this.copyReport}
            className="text-xs px-3 py-1.5 rounded border bg-white hover:bg-muted"
          >
            Copy full error report
          </button>
          <button
            type="button"
            onClick={this.reset}
            className="text-xs px-3 py-1.5 rounded border bg-white hover:bg-muted"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-xs px-3 py-1.5 rounded border bg-white hover:bg-muted"
          >
            Reload page
          </button>
        </div>

        {this.state.fallbackReport ? (
          <div className="rounded border border-red-300 bg-white p-3 text-xs space-y-2">
            <div className="font-semibold text-red-800">
              Clipboard unavailable. Select all and copy manually (⌘/Ctrl+A, ⌘/Ctrl+C).
            </div>
            <textarea
              ref={(el) => {
                if (el) {
                  el.focus();
                  el.select();
                }
              }}
              readOnly
              value={this.state.fallbackReport}
              className="w-full h-48 font-mono text-[11px] border rounded p-2"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              onClick={() => this.setState({ fallbackReport: null })}
              className="text-xs px-3 py-1.5 rounded border bg-white hover:bg-muted"
            >
              Close
            </button>
          </div>
        ) : null}
      </div>

    );
  }
}

export default AdminErrorBoundary;
